import { FormGroup as MuiFormGroup } from "@mui/material";
import { Stack } from "../../Layout";
import { FormControl, FormHelperText, FormLabel } from "../_partials";
import { CheckboxItem } from "./CheckboxItem";

export type CheckboxOption = {
    label: string | React.ReactNode;
    value: string | boolean;
    attributes?: {
        disabled: boolean;
    };
};

type CheckboxFieldProps = {
    alignTop?: boolean;
    helperText?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    isError?: boolean;
    label?: string;
    labelPlacement?: "end" | "start";
    name: string;
} & (
    | {
          options: [CheckboxOption & { value: string | boolean }];
          onChange: (data: string | boolean) => void;
          value: string | boolean;
      }
    | {
          options: CheckboxOption[];
          onChange: (data: string[]) => void;
          value: string[];
      }
);

export function CheckboxField({
    alignTop,
    helperText = "",
    inputRef,
    isError = false,
    label,
    labelPlacement,
    name,
    onChange,
    options,
    value,
}: CheckboxFieldProps) {
    const isSingleOption = options.length === 1;

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!isSingleOption) {
            const newValues = event.target.checked
                ? [...(value as string[]), event.target.value]
                : (value as string[]).filter((val) => val !== event.target.value);

            (onChange as (data: string[]) => void)(newValues);
            return;
        }

        const isBoolean = typeof options[0].value === "boolean";

        let newValue: string | boolean;
        if (isBoolean) {
            newValue = !!event.target.checked;
        } else {
            newValue = event.target.checked ? event.target.value : "";
        }

        (onChange as (data: string | boolean) => void)(newValue);
    };

    return (
        <FormControl isError={isError}>
            <Stack gap={0.5}>
                {label ? <FormLabel htmlFor="">{label}</FormLabel> : null}

                <MuiFormGroup>
                    {isSingleOption ? (
                        <CheckboxItem
                            alignTop={alignTop}
                            checked={value === options[0].value}
                            disabled={options[0].attributes?.disabled}
                            inputRef={inputRef}
                            label={options[0].label}
                            labelPlacement={labelPlacement}
                            name={name}
                            onChange={handleOnChange}
                            value={options[0].value}
                        />
                    ) : (
                        options.map((option) => (
                            <CheckboxItem
                                alignTop={alignTop}
                                checked={
                                    Array.isArray(value)
                                        ? value.includes(option.value as string)
                                        : value === option.value
                                }
                                disabled={option.attributes?.disabled}
                                inputRef={inputRef}
                                key={option.value as string}
                                label={option.label}
                                labelPlacement={labelPlacement}
                                name={name}
                                onChange={handleOnChange}
                                value={option.value}
                            />
                        ))
                    )}
                </MuiFormGroup>

                {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
            </Stack>
        </FormControl>
    );
}
